import { Component, OnInit, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, I18nStore } from '../../core/features/translations';
import { LecturerAvailabilityService, PlannedBlock } from '../../core/services/lecturer-availability.service';
import { AuthService } from '../../services/auth.service';
import { ContactService } from '../../core/services/contact.service';

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  blocks: PlannedBlock[];
}

export interface CalendarMonth {
  name: string;
  year: number;
  weeks: CalendarDay[][];
}

@Component({
  selector: 'app-planning-overview',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe
  ],
  templateUrl: './planning-overview.html',
  styleUrl: './planning-overview.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningPeriodOverviewComponent implements OnInit {
  private readonly lecturerAvailabilityService = inject(LecturerAvailabilityService);
  private readonly authService = inject(AuthService);
  private readonly contactService = inject(ContactService);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(I18nStore);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly rawPlannedBlocks = signal<PlannedBlock[]>([]);
  readonly onlyConfirmed = signal<boolean>(false);
  readonly viewMode = signal<'table' | 'calendar'>('table');
  private currentContactId: number | null = null;
  
  readonly displayedColumns = ['status', 'guaranteed', 'weekday', 'date', 'time', 'module', 'location', 'actions'];

  readonly plannedBlocks = computed(() => {
    const blocks = this.rawPlannedBlocks();
    if (this.onlyConfirmed()) {
      return blocks.filter(b => b.planning_status === 'CONFIRMED');
    }
    return blocks;
  });

  readonly pageTitleText = computed(() => {
    if (this.onlyConfirmed()) {
      const trans = this.i18n.t('planning.confirmed.title');
      return trans === 'planning.confirmed.title' ? 'Bestätigter Einsatzplan' : trans;
    } else {
      const trans = this.i18n.t('planning.overview.title');
      return trans === 'planning.overview.title' ? 'Einsatzplanung Übersicht' : trans;
    }
  });

  readonly pageSubtitleText = computed(() => {
    if (this.onlyConfirmed()) {
      const trans = this.i18n.t('planning.confirmed.subtitle');
      return trans === 'planning.confirmed.subtitle' 
        ? 'Hier sehen Sie alle von Ihnen bestätigten Planungsvorschläge.' 
        : trans;
    } else {
      const trans = this.i18n.t('planning.overview.subtitle');
      return trans === 'planning.overview.subtitle' 
        ? 'Bitte prüfen Sie die folgenden Planungsvorschläge und geben Sie Ihre Rückmeldung.' 
        : trans;
    }
  });

  readonly calendarMonths = computed(() => {
    const blocks = this.plannedBlocks();
    
    let startYear: number;
    let startMonth: number;
    let endYear: number;
    let endMonth: number;

    if (blocks.length === 0) {
      const today = new Date();
      startYear = today.getFullYear();
      startMonth = today.getMonth();
      endYear = today.getFullYear();
      endMonth = today.getMonth();
    } else {
      const dates = blocks.map(b => new Date(b.date));
      const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
      const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

      startYear = minDate.getFullYear();
      startMonth = minDate.getMonth();
      endYear = maxDate.getFullYear();
      endMonth = maxDate.getMonth();
    }

    const months: CalendarMonth[] = [];
    const start = new Date(startYear, startMonth, 1);
    const end = new Date(endYear, endMonth, 1);

    const current = new Date(start);
    let safetyCounter = 0;
    
    while (current <= end && safetyCounter < 12) {
      safetyCounter++;
      const year = current.getFullYear();
      const month = current.getMonth();

      // Number of days in month
      const totalDays = new Date(year, month + 1, 0).getDate();
      
      // Weekday index of first day (0 = Sun, ..., 6 = Sat) -> adjust to Mon-Sun
      let firstDayIndex = new Date(year, month, 1).getDay();
      firstDayIndex = firstDayIndex === 0 ? 6 : firstDayIndex - 1;

      const days: CalendarDay[] = [];

      // Prev month padding
      const prevMonthTotalDays = new Date(year, month, 0).getDate();
      for (let p = firstDayIndex - 1; p >= 0; p--) {
        const d = new Date(year, month - 1, prevMonthTotalDays - p);
        days.push({
          date: d,
          isCurrentMonth: false,
          isToday: this.isToday(d),
          blocks: []
        });
      }

      // Current month days
      for (let d = 1; d <= totalDays; d++) {
        const dateObj = new Date(year, month, d);
        const dateStr = this.formatISODate(dateObj);
        const dayBlocks = blocks.filter(b => b.date === dateStr);

        days.push({
          date: dateObj,
          isCurrentMonth: true,
          isToday: this.isToday(dateObj),
          blocks: dayBlocks
        });
      }

      // Next month padding to fill exactly 6 weeks (42 grid cells)
      const remainingCells = 42 - days.length;
      for (let n = 1; n <= remainingCells; n++) {
        const d = new Date(year, month + 1, n);
        days.push({
          date: d,
          isCurrentMonth: false,
          isToday: this.isToday(d),
          blocks: []
        });
      }

      // Slice into 6 weeks
      const weeks: CalendarDay[][] = [];
      for (let w = 0; w < 6; w++) {
        weeks.push(days.slice(w * 7, (w + 1) * 7));
      }

      const monthNames = [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
      ];

      months.push({
        name: monthNames[month],
        year,
        weeks
      });

      // Move to next month safely by setting date to 1 first
      current.setDate(1);
      current.setMonth(current.getMonth() + 1);
    }

    return months;
  });
  ngOnInit(): void {
    this.onlyConfirmed.set(!!this.route.snapshot.data['onlyConfirmed']);
    this.loadUserAndData();
  }

  private loadUserAndData(): void {
    const userInfo = this.authService.getUserInfo();
    if (!userInfo) {
      this.error.set('Authentication error');
      this.loading.set(false);
      return;
    }

    const email = userInfo.sub || userInfo.email;
    this.contactService.getContactByEmail(email).subscribe({
      next: (contact) => {
        if (contact?.contact_id) {
          this.currentContactId = contact.contact_id;
          this.loadPlannedBlocks(contact.contact_id);
        } else {
          this.error.set('Contact not found');
          this.loading.set(false);
        }
      },
      error: () => {
        this.error.set('Error loading contact');
        this.loading.set(false);
      }
    });
  }

  loadPlannedBlocks(contactId: number): void {
    this.loading.set(true);
    this.lecturerAvailabilityService.getPlannedBlocks(contactId).subscribe({
      next: (blocks) => {
        this.rawPlannedBlocks.set(blocks);
        this.loading.set(false);
      },
      error: (err: any) => {
        this.error.set(err.message);
        this.loading.set(false);
      }
    });
  }

  onAction(block: PlannedBlock, action: 'APPROVE' | 'DECLINE'): void {
    const contactId = this.currentContactId;
    if (!contactId) return;

    this.loading.set(true);
    
    // 1. Perform the planning action (APPROVE -> CONFIRMED, DECLINE -> DECLINED)
    this.lecturerAvailabilityService.lecturerAction(block.lecture_block_id, action).subscribe({
      next: () => {
        // 2. If DECLINE, also remove availability
        if (action === 'DECLINE') {
          this.removeAvailability(contactId, block);
        } else {
          this.loadPlannedBlocks(contactId);
        }
      },
      error: (err: any) => {
        this.error.set(err.message || 'Action failed');
        this.loading.set(false);
      }
    });
  }

  private removeAvailability(contactId: number, block: PlannedBlock): void {
    this.lecturerAvailabilityService.saveDailyAvailability(contactId, {
      school_id: block.school_id,
      location_id: block.location_id,
      variant_id: block.variant_id,
      availability_date: block.date,
      status: 'normal' // Removes the availability entry
    }).subscribe({
      next: () => {
        this.loadPlannedBlocks(contactId);
      },
      error: (err: any) => {
        console.error('Failed to remove availability:', err);
        this.loadPlannedBlocks(contactId);
      }
    });
  }

  exportToICS(): void {
    const blocks = this.plannedBlocks();
    if (blocks.length === 0) return;

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Aetheris//NONSGML Lecturer Portal//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH'
    ];

    blocks.forEach(block => {
      const dateStr = block.date.replace(/-/g, ''); // YYYYMMDD
      const startTimeStr = block.begin_time.replace(/:/g, '') + '00'; // HHMMSS
      const endTimeStr = block.end_time.replace(/:/g, '') + '00'; // HHMMSS

      const dtStart = `${dateStr}T${startTimeStr}`;
      const dtEnd = `${dateStr}T${endTimeStr}`;
      
      const summary = `${block.module} (${block.location})`;
      const guaranteedText = block.is_guaranteed ? 'Ja' : 'Nein';
      const description = `Modul: ${block.module}\\nStandort: ${block.location}\\nStatus: ${block.planning_status}\\nGarantiert: ${guaranteedText}`;

      icsContent.push('BEGIN:VEVENT');
      icsContent.push(`UID:lecture-block-${block.lecture_block_id}@aetheris.com`);
      icsContent.push(`DTSTAMP:${this.formatDateStamp(new Date())}`);
      icsContent.push(`DTSTART;TZID=Europe/Zurich:${dtStart}`);
      icsContent.push(`DTEND;TZID=Europe/Zurich:${dtEnd}`);
      icsContent.push(`SUMMARY:${summary}`);
      icsContent.push(`DESCRIPTION:${description}`);
      icsContent.push('END:VEVENT');
    });

    icsContent.push('END:VCALENDAR');

    const blob = new Blob([icsContent.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'lektionen-plan.ics');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private formatDateStamp(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return date.getUTCFullYear() +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) + 'T' +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      pad(date.getUTCSeconds()) + 'Z';
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  }

  private formatISODate(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
}
